
import React, { createContext, useContext, useState } from 'react';

const AIAgentContext = createContext();

export const AIAgentProvider = ({ children }) => {
  const [isAgentActive, setIsAgentActive] = useState(false);
  const [currentInsight, setCurrentInsight] = useState(null);
  const [analysisLevel, setAnalysisLevel] = useState(0);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatMode, setChatMode] = useState(false); // New state for chat mode

  const toggleAgent = () => {
    setIsAgentActive(!isAgentActive);
    if (!isAgentActive) {
      // Reset the states when activating
      setAnalysisLevel(0);
      setCurrentScenario(null);
    }
  };

  const drillDownAnalysis = (level) => {
    setAnalysisLevel(level);
  };

  const selectScenario = (scenario) => {
    setCurrentScenario(scenario);
  };

  const addChatMessage = (message) => {
    setChatHistory(prev => [...prev, message]);
  };

  const clearChatHistory = () => {
    setChatHistory([]);
  };

  // Toggle between structured mode and chat mode
  const toggleChatMode = () => {
    setChatMode(!chatMode);
  };

  // Simulate AI response to user query
  const processUserQuery = async (query) => {
    setIsProcessing(true);
    
    // Add user message to chat history
    addChatMessage({
      role: 'user',
      content: query,
      timestamp: new Date().toISOString()
    });
    
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Generate AI response based on query content
    let response;
    
    if (query.toLowerCase().includes('performance') || query.toLowerCase().includes('kpi')) {
      response = "Based on current KPIs, network performance is 9.3% above target with strongest growth in urban premium segments. However, campaign ROI requires attention with a 1.1x return vs target of 1.75x.";
    } else if (query.toLowerCase().includes('campaign') || query.toLowerCase().includes('promotion')) {
      response = "There are currently 2 active campaigns vs. 4 planned. The Summer Fuel Promotion is underperforming with ROI at 1.1x vs target 1.75x. Would you like to review campaign details or see recommendations for improvement?";
    } else if (query.toLowerCase().includes('competitor') || query.toLowerCase().includes('competition')) {
      response = "ABC Gas has reduced prices in 28 areas by an average of 0.15 ฿/L in the past 24 hours. This affects primarily urban locations. Based on historical trends, this may impact our volume by 3-5% in affected areas.";
    } else {
      response = "I can help you analyze performance metrics, monitor competitors, optimize campaigns, or explore customer segments. What specific insights are you looking for today?";
    }
    
    // Add AI response to chat history
    addChatMessage({
      role: 'assistant',
      content: response,
      timestamp: new Date().toISOString()
    });
    
    setIsProcessing(false);
  };

  return (
    <AIAgentContext.Provider
      value={{
        isAgentActive,
        toggleAgent,
        currentInsight,
        setCurrentInsight,
        analysisLevel,
        drillDownAnalysis,
        currentScenario,
        setCurrentScenario,
        selectScenario,
        chatHistory,
        addChatMessage,
        clearChatHistory,
        isProcessing,
        setIsProcessing,
        processUserQuery,
        chatMode,
        toggleChatMode
      }}
    >
      {children}
    </AIAgentContext.Provider>
  );
};

export const useAIAgent = () => useContext(AIAgentContext);
